import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Recording } from "../../api/types";
import { parseUtcDate } from "../../utils";
import RecentSession from "./RecentSession";
import SessionFilterControls from "./SessionFilterControls";
import { matchesSessionFilters, sessionFilterCount } from "./sessionFilterModel";
import { useSessionFilters } from "./useActivityFilters";

export default function RecentSessions({
  recordings,
  revision,
}: { recordings: Recording[]; revision: number }) {
  const { t } = useTranslation();
  const { filters, onChange } = useSessionFilters();
  const scope = JSON.stringify(filters);
  const [pagination, setPagination] = useState({ scope, limit: 5 });
  if (pagination.scope !== scope) setPagination({ scope, limit: 5 });
  const limit = pagination.scope === scope ? pagination.limit : 5;
  const count = sessionFilterCount(filters);
  const visible = recordings
    .filter(item => matchesSessionFilters(item, filters, t("dashboard.unnamed_session")))
    .sort(
      (a, b) => parseUtcDate(b.date_started).getTime() - parseUtcDate(a.date_started).getTime(),
    );
  return (
    <section>
      <div className="mb-3">
        <SessionFilterControls
          title={<h3 className="font-semibold">{t("activity.recent_sessions")}</h3>}
          recordings={recordings}
          filters={filters}
          onChange={onChange}
        />
      </div>
      {count > 0 && (
        <p role="status" className="mb-2 text-xs text-text-dim">
          {t("activity.session_matches", {
            count: visible.length,
            total: recordings.length,
          })}
        </p>
      )}
      {visible.length > 0 && (
        <div className="overflow-hidden rounded-lg border border-outline">
          {visible.slice(0, limit).map(item => (
            <RecentSession key={item.id} recording={item} revision={revision} />
          ))}
        </div>
      )}
      {!visible.length && (
        <p className="py-3 text-sm text-text-dim">
          {t(count ? "no_results_match_filters" : "activity.no_member_data")}
        </p>
      )}
      {visible.length > limit && (
        <button
          type="button"
          onClick={() => setPagination({ scope, limit: limit + 5 })}
          className="mt-3 w-full rounded-lg border border-outline bg-surface py-2 text-sm font-medium text-info hover:bg-info/5 focus-visible:outline-info"
        >
          {t("activity.show_more")}
        </button>
      )}
    </section>
  );
}
